import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { supabase } from '@/lib/supabase';
import { useTranslation } from 'react-i18next';
import { buildMcpAuthorizeUrlWithHandoff } from '@/services/mcpOAuthHandoff';

interface OAuthParams {
  oauth_flow?: string;
  client_id?: string;
  redirect_uri?: string;
  response_type?: string;
  scope?: string;
  state?: string;
  code_challenge?: string;
  code_challenge_method?: string;
}

/**
 * AuthenticatePage - Dedicated OAuth flow handler
 * 
 * This route handles OAuth authentication flows by:
 * 1. Checking user authentication status
 * 2. Parsing OAuth parameters from URL
 * 3. Redirecting appropriately based on auth state and OAuth presence
 */
export default function AuthenticatePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, isLoading } = useAuth();
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handleAuthentication = async () => {
      console.log('🔐 AuthenticatePage: Starting OAuth flow analysis');
      console.log('🔐 AuthenticatePage: Auth state:', { isAuthenticated, isLoading });

      // Wait for auth state to be determined
      if (isLoading) {
        console.log('🔐 AuthenticatePage: Waiting for auth state...');
        return;
      }

      try {
        // Parse OAuth parameters from URL
        const oauthParams: OAuthParams = {
          oauth_flow: searchParams.get('oauth_flow') || undefined,
          client_id: searchParams.get('client_id') || undefined,
          redirect_uri: searchParams.get('redirect_uri') || undefined,
          response_type: searchParams.get('response_type') || undefined,
          scope: searchParams.get('scope') || undefined,
          state: searchParams.get('state') || undefined,
          code_challenge: searchParams.get('code_challenge') || undefined,
          code_challenge_method: searchParams.get('code_challenge_method') || undefined,
        };

        console.log('🔐 AuthenticatePage: Parsed OAuth params:', oauthParams);

        // Check if this is a valid OAuth flow
        const isOAuthFlow = oauthParams.oauth_flow === 'mcp';
        const hasRequiredParams = oauthParams.client_id && 
                                 oauthParams.redirect_uri && 
                                 oauthParams.response_type;

        console.log('🔐 AuthenticatePage: OAuth validation:', { 
          isOAuthFlow, 
          hasRequiredParams,
          flow: oauthParams.oauth_flow 
        });

        // Scenario 1: No OAuth flow - redirect to dashboard
        if (!isOAuthFlow || !hasRequiredParams) {
          console.log('🔐 AuthenticatePage: No valid OAuth flow detected, redirecting to dashboard');
          let target = '/dashboard';
          try {
            const stored = sessionStorage.getItem('memara_post_login_redirect');
            if (stored && stored.startsWith('/')) {
              target = stored;
              sessionStorage.removeItem('memara_post_login_redirect');
            }
          } catch (_) {}
          navigate(target, { replace: true });
          return;
        }

        // Scenario 2: Valid OAuth flow detected
        console.log('🔐 AuthenticatePage: Valid MCP OAuth flow detected');

        if (isAuthenticated) {
          console.log('🔐 AuthenticatePage: User is authenticated, preparing MCP redirect');

          // Get access token for MCP server authentication
          const { data: sessionData } = await supabase.auth.getSession();
          
          const session = sessionData.session;
          if (!session?.access_token || !session.refresh_token) {
            console.error('🔐 AuthenticatePage: No session tokens found despite authenticated state');
            setError('Authentication token not available. Please try signing in again.');
            return;
          }

          const mcpParams: Record<string, string> = {};
          
          if (oauthParams.client_id) mcpParams.client_id = oauthParams.client_id;
          if (oauthParams.redirect_uri) mcpParams.redirect_uri = oauthParams.redirect_uri;
          if (oauthParams.response_type) mcpParams.response_type = oauthParams.response_type;
          if (oauthParams.scope) mcpParams.scope = oauthParams.scope;
          if (oauthParams.state) mcpParams.state = oauthParams.state;
          if (oauthParams.code_challenge) mcpParams.code_challenge = oauthParams.code_challenge;
          if (oauthParams.code_challenge_method) mcpParams.code_challenge_method = oauthParams.code_challenge_method;

          const mcpAuthUrl = await buildMcpAuthorizeUrlWithHandoff(
            session.access_token,
            session.refresh_token,
            mcpParams
          );
          
          console.log('🔐 AuthenticatePage: Redirecting authenticated user to MCP authorization');
          window.location.href = mcpAuthUrl;
          
        } else {
          console.log('🔐 AuthenticatePage: User not authenticated, redirecting to login with OAuth params');

          // Preserve OAuth parameters in login redirect
          const loginUrl = `/login?${searchParams.toString()}`;
          console.log('🔐 AuthenticatePage: Login URL with OAuth params:', loginUrl);
          
          navigate(loginUrl, { replace: true });
        }

      } catch (error) {
        console.error('🔐 AuthenticatePage: Error processing OAuth flow:', error);
        setError('Authentication flow error. Please try again.');
      }
    };

    handleAuthentication();
  }, [isAuthenticated, isLoading, searchParams, navigate]);

  // Error state
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="max-w-md w-full space-y-4 p-6 text-center">
          <div className="text-red-600 mb-4">
            <h2 className="text-xl font-semibold">{t('authenticationError', { ns: 'auth' })}</h2>
            <p className="text-sm mt-2">{error}</p>
          </div>
          <button
            onClick={() => navigate('/login', { replace: true })}
            className="w-full bg-primary text-primary-foreground hover:bg-primary/90 px-4 py-2 rounded-md transition-colors"
          >
            {t('backToLogin', { ns: 'auth' })}
          </button>
        </div>
      </div>
    );
  }

  // Loading state
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="max-w-md w-full space-y-4 p-6 text-center">
        <div className="flex justify-center mb-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
        <h2 className="text-xl font-semibold text-foreground">
          {t('authenticating', { ns: 'auth' })}
        </h2>
        <p className="text-muted-foreground">
          {t('checkingAuthentication', { ns: 'auth' })}
        </p>
      </div>
    </div>
  );
}
